import { PageHeader } from '../components/office';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { settingsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './backoffice/settings/Section';
import FinancialCalendarCard from './backoffice/settings/FinancialCalendarCard';
import InsightCard from './backoffice/settings/InsightCard';
import CashCard from './backoffice/settings/CashCard';
import TillsCard from './backoffice/settings/TillsCard';
import SignInCard from './backoffice/settings/SignInCard';
import PrintersCard from './backoffice/settings/PrintersCard';

// Company (Back office sweep, Oct 2026: D14). Each section saves itself: The business with
// its own button, the alerts as they're switched, and every card below on its own. The
// Daily report email and dark mode switches are gone; nothing ever read them.

// The clocks a venue runs on, by name (Chris's review L20: "Europe/London" in a text box).
// A zone saved before that isn't here stays, shown as it is.
const TIMEZONES = [
  ['Europe/London', 'UK time (London)'],
  ['Europe/Dublin', 'Ireland (Dublin)'],
  ['Europe/Paris', 'Central Europe (Paris, Madrid, Berlin)'],
  ['Europe/Lisbon', 'Portugal (Lisbon)'],
  ['Atlantic/Canary', 'Canary Islands'],
];

const Field = ({ id, label, hint, required, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-[12.5px] text-ink-soft">{label}{required && <span className="text-ink-faint"> (required)</span>}</Label>
    {children}
    {hint && <p className="text-[12px] text-ink-faint">{hint}</p>}
  </div>
);

const SettingsPage = () => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  // The form is controlled, and the fetch below replaces every field when it lands. Until
  // it has, typing into it is typing into something about to be overwritten — the edit
  // disappears, Save then persists the value that was already there, and the toast says
  // "Settings saved". Nothing tells you. So the fields wait for their own contents.
  const [loaded, setLoaded] = useState(false);

  const [settings, setSettings] = useState({
    restaurantName: '', address: '', phone: '', email: '', taxRate: '0', vatNumber: '',
    receiptFooter: '', currency: 'GBP', timezone: 'Europe/London',
    notifications: { lowStock: true, newOrders: true },
  });
  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  // Load the real persisted org settings on mount (P5-SETTINGS).
  useEffect(() => {
    let cancelled = false;
    settingsApi.get().then((res) => {
      if (cancelled) return;
      const s = res.data || {};
      setLoaded(true);
      setSettings({
        restaurantName: s.restaurant_name ?? '',
        address: s.address ?? '',
        phone: s.phone ?? '',
        email: s.email ?? '',
        taxRate: String(Math.round((s.vat_rate ?? 0) * 10000) / 100), // decimal → %
        vatNumber: s.vat_number ?? '',
        receiptFooter: s.receipt_footer ?? '',
        currency: s.currency ?? 'GBP',
        timezone: s.timezone ?? 'Europe/London',
        notifications: {
          lowStock: s.notifications?.low_stock ?? true,
          newOrders: s.notifications?.new_orders ?? true,
        },
      });
    }).catch(() => {
      // A failed load must not leave the form frozen: there is nothing to overwrite it
      // with, so let it be edited.
      setLoaded(true);
      toast.error('Could not load settings');
    });
    return () => { cancelled = true; };
  }, []);

  // Real persistence — no simulated delay, and a success toast only on a real 200 (STUB-2).
  const handleSave = async () => {
    if (!settings.restaurantName.trim()) { toast.error('The venue needs a name: it heads every receipt'); return; }
    setSaving(true);
    try {
      await settingsApi.update({
        restaurant_name: settings.restaurantName,
        address: settings.address,
        phone: settings.phone,
        email: settings.email,
        vat_rate: (parseFloat(settings.taxRate) || 0) / 100, // % → decimal
        // Both print on every receipt (`receipt_render`, `escpos`); a UK VAT receipt needs
        // the number. The API has taken them since #126, but nothing here could set them.
        vat_number: settings.vatNumber.trim(),
        receipt_footer: settings.receiptFooter,
        currency: settings.currency,
        timezone: settings.timezone,
      });
      toast.success('Settings saved');
    } catch {
      toast.error('Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  // An alert saves as it's switched, on its own; the rest of the form isn't touched.
  const setAlert = async (key, on) => {
    const next = { ...settings.notifications, [key]: on };
    setSettings({ ...settings, notifications: next });
    try {
      await settingsApi.update({ notifications: { low_stock: next.lowStock, new_orders: next.newOrders } });
    } catch {
      setSettings((cur) => ({ ...cur, notifications: { ...cur.notifications, [key]: !on } }));
      toast.error('Could not save that');
    }
  };

  return (
    <div className="max-w-4xl space-y-5" data-testid="settings-page">
      <PageHeader
        title="Company"
        purpose="The business: name, receipts, tax, the financial calendar, the tills and Verlo Insight. Each section saves on its own."
      />

      <Card>
        <CardHeader>
          <CardTitle>The business</CardTitle>
          <CardDescription>Who you are, what your receipts say, and the money and time you trade in.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field id="restaurantName" label="Name" required>
              <Input id="restaurantName" value={settings.restaurantName} disabled={!loaded} onChange={set('restaurantName')} data-testid="setting-restaurant-name" /></Field>
            <Field id="phone" label="Phone">
              <Input id="phone" value={settings.phone} disabled={!loaded} onChange={set('phone')} data-testid="setting-phone" /></Field>
            <Field id="address" label="Address">
              <Input id="address" value={settings.address} disabled={!loaded} onChange={set('address')} data-testid="setting-address" /></Field>
            <Field id="email" label="Email">
              <Input id="email" type="email" value={settings.email} disabled={!loaded} onChange={set('email')} data-testid="setting-email" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-4 border-t border-hairline pt-4 md:grid-cols-2">
            <Field id="vatNumber" label="VAT number" hint="Printed on every receipt. A UK VAT receipt needs it.">
              <Input id="vatNumber" value={settings.vatNumber} disabled={!loaded} maxLength={40} placeholder="e.g. GB 123 4567 89" onChange={set('vatNumber')} data-testid="setting-vat-number" /></Field>
            <Field id="receiptFooter" label="Receipt footer" hint="The last line of every receipt, printed and emailed.">
              <Input id="receiptFooter" value={settings.receiptFooter} disabled={!loaded} maxLength={200} placeholder="e.g. Thank you" onChange={set('receiptFooter')} data-testid="setting-receipt-footer" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-4 border-t border-hairline pt-4 md:grid-cols-3">
            <Field id="currency" label="Currency">
              <Input id="currency" value={settings.currency} disabled={!loaded} onChange={set('currency')} data-testid="setting-currency" /></Field>
            <Field id="taxRate" label="VAT rate (%)">
              <Input id="taxRate" type="number" min="0" max="100" step="0.1" value={settings.taxRate} disabled={!loaded} onChange={set('taxRate')} className="font-mono" data-testid="setting-tax-rate" /></Field>
            <Field id="timezone" label="Timezone">
              <Select value={settings.timezone} disabled={!loaded} onValueChange={(v) => setSettings((s) => ({ ...s, timezone: v }))}>
                <SelectTrigger id="timezone" data-testid="setting-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(TIMEZONES.some(([z]) => z === settings.timezone) ? TIMEZONES : [...TIMEZONES, [settings.timezone, settings.timezone]]).map(([z, words]) => (
                    <SelectItem key={z} value={z}>{words}</SelectItem>
                  ))}
                </SelectContent>
              </Select></Field>
          </div>
          <div className="flex justify-end">
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={handleSave} disabled={saving || !loaded} data-testid="save-settings-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Financial calendar — Verlo Service 6a. Self-contained: own load + save. */}
      <FinancialCalendarCard />
      <CashCard />
      <TillsCard />
      <SignInCard />
      <PrintersCard />
      <InsightCard />

      <Card>
        <CardHeader>
          <CardTitle>Alerts</CardTitle>
          <CardDescription>What the back office tells everyone signed in to it, as it happens. Each saves as you switch it; anyone can switch both off for themselves in My settings.</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-hairline py-1">
          <label className="flex items-center justify-between gap-4 py-3">
            <span className="text-[14px] text-ink">Running low<span className="block text-[12.5px] text-ink-soft">A note when stock falls to its low mark, with a way to order it.</span></span>
            <Switch checked={settings.notifications.lowStock} disabled={!loaded} onCheckedChange={(v) => setAlert('lowStock', v)} data-testid="setting-low-stock-alert" />
          </label>
          <label className="flex items-center justify-between gap-4 py-3">
            <span className="text-[14px] text-ink">New orders<span className="block text-[12.5px] text-ink-soft">A note and a chime when the till takes an order.</span></span>
            <Switch checked={settings.notifications.newOrders} disabled={!loaded} onCheckedChange={(v) => setAlert('newOrders', v)} data-testid="setting-new-order-alert" />
          </label>
        </CardContent>
      </Card>

      {/* Verlo's own staff, working in this restaurant's portal (#180) */}
      {user?.platform_role && (
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Shown to Verlo staff only.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Notice tone="info" word="Verlo staff" title={user?.email}>
              You are in this restaurant&apos;s portal with its owner&apos;s access, including staff
              management and data export.
            </Notice>
            {/* Two-factor lives with the rest of your own sign-in, on My settings (#179). The
                dead Change Password / 2FA buttons that were here went in P5-SETTINGS. */}
            <p className="text-[13.5px] text-ink-soft">
              Two-factor sign-in is on <Link to="/back-office/user-settings" className="text-verlo hover:underline"
                data-testid="settings-two-factor-link">My settings</Link>. Verlo staff have to keep it on.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
